import React, { useRef } from 'react';
import {
  mergeProps,
  useFocusRing,
  usePress,
  useSwitch,
  VisuallyHidden,
} from 'react-aria';
import { useToggleState } from 'react-stately';
import * as S from './styles';
import SwitchStatusLabel from './SwitchStatusLabel';
import { SwitchProps } from './types';

/**
 * Switch / Toggle component.
 *
 * The component's label is rendered as children.  If children is not passed,
 * the component either needs an aria-label prop or an external label.  If an
 * external label is used, this component must have an aria-labelledby prop
 * pointing to the label.
 */
function Switch<As extends React.ElementType = 'label'>(
  props: SwitchProps<As>,
) {
  const {
    'aria-labelledby': labelledBy,
    as,
    children,
    className,
    isDisabled,
    isMobile,
    labelPosition = 'after',
  } = props;

  const Component: React.ElementType = as ?? 'label';

  const inputRef = useRef<HTMLInputElement>(null);

  const state = useToggleState(props);
  const { inputProps } = useSwitch(props, state, inputRef);
  const { focusProps } = useFocusRing();

  // usePress is used so that clicking on the toggle component works when using
  // an external label.  if state.toggle is not called when the toggle component is
  // clicked, the toggle won't toggle
  const { pressProps } = usePress({
    onPress: !labelledBy ? undefined : state.toggle,
  });

  const switchStyleProps = {
    $isSelected: state.isSelected,
    $isMobile: !!isMobile,
  };

  return (
    <S.Root
      as={Component}
      className={className}
      $isDisabled={!!isDisabled}
      $labelPosition={labelPosition}
    >
      <VisuallyHidden>
        <input {...mergeProps(inputProps, focusProps)} ref={inputRef} />
      </VisuallyHidden>
      <S.Track aria-hidden="true" {...switchStyleProps} {...pressProps}>
        {!isMobile && (
          <SwitchStatusLabel
            isSelected={state.isSelected}
            labelType="selected"
          />
        )}
        <S.Thumb {...switchStyleProps} />
        {!isMobile && (
          <SwitchStatusLabel
            isSelected={state.isSelected}
            labelType="unselected"
          />
        )}
      </S.Track>
      {children}
    </S.Root>
  );
}

export default Switch;
